// src/modules/assetManagement/management/useUploadQueue.ts
// Upload pipeline: validate → upload (bytes sent) → processing (server stores/transcodes) → link. Every failed attempt is logged.
import { useCallback, useEffect, useRef, useState } from "react";
import { assetApi, uploadFile, UploadError } from "../assetApi";
import type { AssetKind, UploadStage } from "../types";
import { ACCEPTED_EXTENSIONS, extensionOf, formatBytes, MAX_ATTEMPTS, MAX_FILE_BYTES } from "../utils";

export type QueueStatus = "invalid" | "ready" | "uploading" | "processing" | "done" | "failed";

export interface QueueItem {
  key: string;
  file: File;
  kind: AssetKind;
  status: QueueStatus;
  progress: number;
  attempts: number;
  stage?: UploadStage;
  errorCode?: string;
  error?: string;
  assetId?: string;
  ticketRef?: string;
  /** true once this attempt has been handed to the uploader */
  started?: boolean;
  options?: UploadOptions;
}

export interface UploadOptions {
  tags: string[];
  eventIds: string[];
  productIds: string[];
}

const CONCURRENCY = 3;

export const fileKeyOf = (f: File) => `${f.name}-${f.size}-${f.lastModified}`;

const validate = (file: File): { code: string; message: string } | null => {
  const ext = extensionOf(file.name);
  if (!ACCEPTED_EXTENSIONS[ext]) {
    return { code: "UNSUPPORTED_FORMAT", message: `.${ext || "unknown"} files are not on the supported formats list` };
  }
  if (file.size === 0) return { code: "EMPTY_FILE", message: "The file is empty (0 bytes)" };
  if (file.size > MAX_FILE_BYTES) {
    return { code: "FILE_TOO_LARGE", message: `File is ${formatBytes(file.size)}; the limit is ${formatBytes(MAX_FILE_BYTES)} per file` };
  }
  return null;
};

export function useUploadQueue() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const aborts = useRef(new Map<string, () => void>());

  const patch = useCallback((key: string, change: Partial<QueueItem>) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...change } : i)));
  }, []);

  const logFailure = (item: QueueItem) =>
    assetApi
      .logUploadFailure({
        fileKey: item.key,
        fileName: item.file.name,
        sizeBytes: item.file.size,
        mimeType: item.file.type || "application/octet-stream",
        attempt: Math.max(item.attempts, 1),
        stage: item.stage ?? "upload",
        errorCode: item.errorCode ?? "UNKNOWN",
        errorMessage: item.error ?? "Unknown error",
      })
      .catch((e) => console.error("Could not log upload failure", e));

  const addFiles = useCallback((files: FileList | File[]) => {
    const existing = new Set(itemsRef.current.map((i) => i.key));
    const added: QueueItem[] = [];
    Array.from(files).forEach((file) => {
      const key = fileKeyOf(file);
      if (existing.has(key)) return;
      existing.add(key);
      const problem = validate(file);
      const item: QueueItem = {
        key,
        file,
        kind: ACCEPTED_EXTENSIONS[extensionOf(file.name)] ?? "document",
        status: problem ? "invalid" : "ready",
        progress: 0,
        attempts: problem ? 1 : 0,
        stage: problem ? "validation" : undefined,
        errorCode: problem?.code,
        error: problem?.message,
      };
      if (problem) void logFailure(item);
      added.push(item);
    });
    setItems((prev) => [...prev, ...added]);
  }, []);

  const start = useCallback((options: UploadOptions) => {
    setItems((prev) =>
      prev.map((i) => (i.status === "ready" ? { ...i, status: "uploading", progress: 0, options, started: false } : i)),
    );
  }, []);

  const retry = useCallback((key: string) => {
    setItems((prev) =>
      prev.map((i) =>
        i.key === key && i.status === "failed" && i.attempts < MAX_ATTEMPTS
          ? { ...i, status: "uploading", progress: 0, error: undefined, errorCode: undefined, stage: undefined, started: false }
          : i,
      ),
    );
  }, []);

  const remove = useCallback((key: string) => {
    aborts.current.get(key)?.();
    aborts.current.delete(key);
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clearFinished = useCallback(
    () => setItems((prev) => prev.filter((i) => i.status !== "done" && i.status !== "invalid")),
    [],
  );
  const markTicket = useCallback((key: string, ticketRef: string) => patch(key, { ticketRef }), [patch]);

  /** One attempt: upload, then link to the chosen events/products/tags */
  const run = useCallback(
    async (item: QueueItem) => {
      const attempts = item.attempts + 1;
      patch(item.key, { started: true, attempts, progress: 0 });
      const fail = (stage: UploadStage, errorCode: string, error: string, assetId?: string) => {
        const failed: QueueItem = { ...item, attempts, status: "failed", stage, errorCode, error, assetId, started: false };
        patch(item.key, failed);
        void logFailure(failed);
      };

      const o = item.options;
      const link = (assetId: string) =>
        o && (o.eventIds.length || o.productIds.length || o.tags.length)
          ? assetApi.linkAssets([assetId], o.eventIds, o.productIds, o.tags)
          : Promise.resolve(true);

      let assetId: string;
      try {
        const handle = uploadFile(
          item.file,
          (progress) => patch(item.key, { progress }),
          () => patch(item.key, { progress: 100, status: "processing" }),
        );
        aborts.current.set(item.key, handle.abort);
        assetId = await handle.promise;
      } catch (e) {
        aborts.current.delete(item.key);
        if (e instanceof UploadError && e.code === "CANCELLED") return;
        const err = e instanceof UploadError ? e : new UploadError(String(e), "UNKNOWN", "upload");
        // Stored but not processed: keep its tags, but no event/product links until it is ready
        if (err.assetId && o?.tags.length) await assetApi.linkAssets([err.assetId], [], [], o.tags).catch(() => undefined);
        return fail(err.stage, err.code, err.message, err.assetId);
      }
      aborts.current.delete(item.key);

      try {
        await link(assetId);
      } catch (e) {
        // The file is stored; only the links failed. It can be linked from Manage assets.
        return fail("linking", "LINK_FAILED", e instanceof Error ? e.message : "Linking failed", assetId);
      }
      patch(item.key, { status: "done", progress: 100, assetId });
      if (attempts > 1) void assetApi.resolveFileAttempts(item.key, "retried_ok").catch(() => undefined);
    },
    [patch],
  );

  // Start waiting uploads, CONCURRENCY at a time
  useEffect(() => {
    const busy = items.filter((i) => (i.status === "uploading" || i.status === "processing") && i.started).length;
    const waiting = items.filter((i) => i.status === "uploading" && !i.started).slice(0, Math.max(0, CONCURRENCY - busy));
    waiting.forEach((i) => void run(i));
  }, [items, run]);

  // Cancel in-flight uploads when the queue goes away
  useEffect(() => () => aborts.current.forEach((abort) => abort()), []);

  return { items, addFiles, start, retry, remove, clearFinished, markTicket };
}